// Decide how to display a LOCAL track's artist string using the music knowledge
// base (djpal.resolveArtistNames). You can't tell a collab from a duo/band by a
// rule — "&" appears in "Adrian Hour & Eelke Kleijn" (split), "Pig & Dan" (one
// act), and inside "Claptone, Peter Bjorn & John" ("Peter Bjorn & John" is a
// band). So we resolve candidate names against the DB and break conservatively:
//
//   1. Split on "feat./ft."         → the featured artist is always separate
//   2. Split those on commas        → strongest separator between distinct acts
//   3. Per comma-segment: if the WHOLE segment resolves, keep it (a band/duo
//      like "Peter Bjorn & John" / "Ost & Kjex" / "Pig & Dan"); only if it does
//      NOT resolve but all its "&"-members do → split it. Otherwise leave as-is.

const FEAT_RE = /\s+(?:feat|ft|featuring)\.?\s*/i;
const COMMA_RE = /\s*,\s*/;
// A separating "&"/"and" must have whitespace on BOTH sides. This keeps
// glued-in names intact: "&ME" (the artist), "AC/DC", "Simon&Him" are never
// broken — only " & " / " and " between distinct artists is a separator.
const AMP_RE = /\s+&\s+|\s+and\s+/i;

// "feat./ft." credits → separate segments, then split each on commas.
function topLevelSegments(name) {
    const s = String(name || '').trim();
    if (!s) return [];
    const segs = [];
    for (const part of s.split(FEAT_RE)) {
        for (const seg of part.split(COMMA_RE).map((x) => x.trim()).filter(Boolean)) segs.push(seg);
    }
    return segs;
}

function ampParts(seg) {
    return String(seg || '').split(AMP_RE).map((x) => x.trim()).filter(Boolean);
}

// "&" and "and" are interchangeable in act names but the DB stores them
// inconsistently ("Pig & Dan" vs "Peter Bjorn and John"), and the server
// normalizer drops "&" while keeping the word "and" — so a band stored with
// "and" won't match a "&" input. Resolve/check BOTH spellings.
function ampAndVariants(name) {
    const v = new Set([name]);
    if (/\s&\s/.test(name)) v.add(name.replace(/\s+&\s+/g, ' and ')); // only spaced "&" (not "&ME")
    if (/\sand\s/i.test(name)) v.add(name.replace(/\s+and\s+/ig, ' & '));
    return [...v];
}

// True when a string could hold more than one artist (worth resolving).
function isMultiArtist(raw) {
    const segs = topLevelSegments(raw);
    return segs.length > 1 || (segs.length === 1 && ampParts(segs[0]).length > 1);
}

// Candidates to resolve: only the "&"/"and" segments (to decide keep-as-act vs
// split). We do NOT need to resolve the individual members — the decision is
// "is the COMBINED name a real act?". Both spellings are checked. Deduped.
export function collectResolveCandidates(rawStrings) {
    const set = new Set();
    for (const raw of rawStrings) {
        if (!isMultiArtist(raw)) continue;
        for (const seg of topLevelSegments(raw)) {
            if (ampParts(seg).length > 1) {
                for (const v of ampAndVariants(seg)) set.add(v);
            }
        }
    }
    return [...set];
}

// Map<rawString, string[]> — the display parts (1 = render whole, >1 = separate
// clickable artists). Strategy: SPLIT by default; keep an "A & B" segment whole
// ONLY when the combined name resolves as a real act (a duo/band like Pig & Dan
// or Peter Bjorn and John). This splits genuine collabs even when a member isn't
// in the DJ-focused DB (e.g. "Chelsea Wolfe & Maceo Plex"), while never breaking
// a known act.
export function computeArtistParts(rawStrings, knownNames) {
    const known = new Set((knownNames || []).map((s) => String(s).toLowerCase()));
    const isKnownAct = (n) => ampAndVariants(String(n)).some((v) => known.has(v.toLowerCase()));
    const out = new Map();
    for (const raw of rawStrings) {
        if (!isMultiArtist(raw)) { out.set(raw, [raw]); continue; }
        const result = [];
        for (const seg of topLevelSegments(raw)) {
            const parts = ampParts(seg);
            if (parts.length > 1 && !isKnownAct(seg)) result.push(...parts); // collab → split
            else result.push(seg);                                            // single name or known act → keep
        }
        out.set(raw, result.length > 1 ? result : [raw]);
    }
    return out;
}
